import { PartnerDecision, PartnerDecisionCause } from "@bbj-types/socialApply/enum";
import { Box, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { PARTNER_DECISION, PARTNER_DECISION_CAUSE, PARTNER_REASONS } from "@root/utils/constants/fup";
import { DateInputForm, StackGrid } from "@styleguide";
import { useEffect, useRef } from "react";
import { type Control, Controller, type UseFormResetField, useWatch } from "react-hook-form";
import type { TPartnerFeedbackFormResolver } from "./resolver";

interface IPartnerFeedbackDecisionFormmProps {
  control: Control<TPartnerFeedbackFormResolver>;
  resetField: UseFormResetField<TPartnerFeedbackFormResolver>;
}

export const PartnerFeedbackDecisionForm = ({ control, resetField }: IPartnerFeedbackDecisionFormmProps) => {
  const [partnerDecision, cause] = useWatch({
    control,
    name: ["partnerFeedback.decision.partnerDecision", "partnerFeedback.decision.cause"],
  });
  const noAnswerSectionRef = useRef<HTMLDivElement | null | undefined>();
  useEffect(() => {
    if (cause === PartnerDecisionCause.NO_ANSWER) {
      noAnswerSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [cause]);

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <Typography variant="h4" color="primary">
        Décision du partenaire sur la prescription
      </Typography>
      <StackGrid perLine={{ tablet: 3 }} gap={2}>
        <Controller
          control={control}
          name="partnerFeedback.decision.partnerDecision"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <TextField
              select
              label="Décision du partenaire *"
              value={value ?? ""}
              onChange={(e) => {
                if (e.target.value !== partnerDecision) {
                  resetField("partnerFeedback.decision.assessmentDate");
                  resetField("partnerFeedback.decision.firstReceptionDate");
                  resetField("partnerFeedback.decision.cause");
                  resetField("partnerFeedback.decision.firstConvocation");
                  resetField("partnerFeedback.decision.secondConvocation");
                  resetField("partnerFeedback.decision.thirdConvocation");
                }
                onChange(e);
              }}
              size="small"
              error={Boolean(error)}
              helperText={error?.message}
            >
              {Object.entries(PARTNER_DECISION).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        {Boolean(partnerDecision) &&
          (partnerDecision === PartnerDecision.INTEGRATED ? (
            <>
              <DateInputForm
                control={control}
                name="partnerFeedback.decision.firstReceptionDate"
                label="Date de premier accueil"
                TextFieldProps={{ size: "small" }}
              />
              <DateInputForm
                control={control}
                name="partnerFeedback.decision.assessmentDate"
                label="Bilan prévu le"
                TextFieldProps={{ size: "small" }}
              />
            </>
          ) : (
            <Controller
              control={control}
              name="partnerFeedback.decision.cause"
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <TextField
                  select
                  label="Motif *"
                  value={value ?? ""}
                  onChange={(e) => {
                    if (e.target.value !== partnerDecision) {
                      resetField("partnerFeedback.decision.firstConvocation");
                      resetField("partnerFeedback.decision.secondConvocation");
                      resetField("partnerFeedback.decision.thirdConvocation");
                    }
                    onChange(e);
                  }}
                  size="small"
                  error={Boolean(error)}
                  helperText={error?.message}
                >
                  {Object.entries(PARTNER_DECISION_CAUSE).map(([key, value]) => (
                    <MenuItem key={key} value={key}>
                      {value}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          ))}
      </StackGrid>
      {cause === PartnerDecisionCause.NO_ANSWER && (
        <Box ref={noAnswerSectionRef}>
          <Stack
            sx={{
              gap: 2,
            }}
          >
            <StackGrid perLine={{ tablet: 3 }} gap={2}>
              <DateInputForm
                control={control}
                name="partnerFeedback.decision.firstConvocation.date"
                label="1ère convocation"
                TextFieldProps={{ size: "small" }}
              />
              <Controller
                control={control}
                name="partnerFeedback.decision.firstConvocation.reason"
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <TextField
                    select
                    label="Raison"
                    value={value ?? ""}
                    onChange={onChange}
                    size="small"
                    error={Boolean(error)}
                    helperText={error?.message}
                  >
                    {Object.entries(PARTNER_REASONS).map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {value}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </StackGrid>
            <StackGrid perLine={{ tablet: 3 }} gap={2}>
              <DateInputForm
                control={control}
                name="partnerFeedback.decision.secondConvocation.date"
                label="2ème convocation"
                TextFieldProps={{ size: "small" }}
              />
              <Controller
                control={control}
                name="partnerFeedback.decision.secondConvocation.reason"
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <TextField
                    select
                    label="Raison"
                    value={value ?? ""}
                    onChange={onChange}
                    size="small"
                    error={Boolean(error)}
                    helperText={error?.message}
                  >
                    {Object.entries(PARTNER_REASONS).map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {value}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </StackGrid>
            <StackGrid perLine={{ tablet: 3 }} gap={2}>
              <DateInputForm
                control={control}
                name="partnerFeedback.decision.thirdConvocation.date"
                label="3ème convocation"
                TextFieldProps={{ size: "small" }}
              />
              <Controller
                control={control}
                name="partnerFeedback.decision.thirdConvocation.reason"
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <TextField
                    select
                    label="Raison"
                    value={value ?? ""}
                    onChange={onChange}
                    size="small"
                    error={Boolean(error)}
                    helperText={error?.message}
                  >
                    {Object.entries(PARTNER_REASONS).map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {value}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </StackGrid>
          </Stack>
        </Box>
      )}
    </Stack>
  );
};
